import Vue from "vue";
import router from "@/router";

const LOGOUT_WARNING_THRESHOLD = 300; // number of seconds left to token expiration

const state = {
    access_token: sessionStorage.getItem("access_token") || "",
    refresh_token: sessionStorage.getItem("refresh_token") || "",
    expires_at: Number(sessionStorage.getItem("token_expires_at")) || "",

    showSessionWarning: false,
    now: Math.floor(Date.now() / 1000),
    sessionTimerStarted: false,
};

const actions = {
    loginRequest: ({ commit, dispatch }, payload) => {
        const data = {
            email: payload.email,
            password: payload.password,
        };

        return new Promise((resolve, reject) => {
            commit("loginRequest");
            Vue.axios
                .post("/auth/login", data)
                .then(async resp => {
                    commit("loginSuccess", resp.data);
                    await dispatch("getMe");
                    resolve(resp.data.access_token);
                })
                .catch((err) => {
                    let errorMessage;

                    if (err && err.response && err.response.data && err.response.data.message) {
                        errorMessage = err.response.data.message;
                        if (err.response.data.errors && Object.keys(err.response.data.errors).length) {
                            errorMessage += " Please correct the following:";

                            for (const prop in err.response.data.errors) {
                                errorMessage += " " + err.response.data.errors[prop][0];
                            }
                        }
                    } else if (err) {
                        errorMessage = err;
                    } else {
                        errorMessage = "An unknown error occurred";
                    }

                    dispatch("handleError", errorMessage);
                    reject(err);
                });
        });
    },

    logout: async ({ commit, dispatch }) => {
        await commit("logout");
        dispatch("clearCurrentUser");
        router.push("/login");
    },

    validateUserRegistrationToken: ({commit, dispatch}, token) => {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`/users/register/${token}`)
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },

    completeUserRegistration: ({commit, dispatch}, payload) => {
        return new Promise((resolve, reject) => {
            Vue.axios
                .post("/users/register", payload)
                .then(resp => {
                    resolve(resp);
                })
                .catch(err => {
                    let errorMessage;

                    if (err && err.response && err.response.data && err.response.data.message) {
                        errorMessage = err.response.data.message;
                        if (err.response.data.errors && Object.keys(err.response.data.errors).length) {
                            errorMessage += " Please correct the following:";

                            for (const prop in err.response.data.errors) {
                                errorMessage += " " + err.response.data.errors[prop][0];
                            }
                        }
                    } else if (err) {
                        errorMessage = err;
                    } else {
                        errorMessage = "An unknown error occurred";
                    }

                    dispatch("handleError", errorMessage);
                    reject(err);
                });
        });
    },

    forgotPassword: ({dispatch}, payload) => {
        return new Promise((resolve, reject) => {
            Vue.axios.post("/users/forgot-password", payload)
                .then(() => {
                    dispatch("handleSuccess", "Please check your email for a link to reset your password");
                    resolve(true);
                })
                .catch(err => {
                    let errorMessage;

                    if (err && err.response && err.response.data && err.response.data.message) {
                        errorMessage = err.response.data.message;
                        if (err.response.data.errors && Object.keys(err.response.data.errors).length) {
                            errorMessage += " Please correct the following:";

                            for (const prop in err.response.data.errors) {
                                errorMessage += " " + err.response.data.errors[prop][0];
                            }
                        }
                    } else if (err) {
                        errorMessage = err;
                    } else {
                        errorMessage = "An unknown error occurred";
                    }

                    dispatch("handleError", errorMessage);
                    reject(err);
                });
        });
    },

    resetPassword: ({dispatch}, payload) => {
        return new Promise((resolve, reject) => {
            Vue.axios.post(`/users/reset-password/${payload.token}`, payload)
                .then(resp => {
                    dispatch("handleSuccess", "Your password has been reset successfully, please login using your new password.");
                    resolve(resp);
                })
                .catch(err => {
                    let errorMessage;

                    if (err && err.response && err.response.data) {
                        errorMessage = err.response.data.message;

                        if (err.response.data.errors && Object.keys(err.response.data.errors).length) {
                            for (let key in err.response.data.errors) {
                                errorMessage += (" " + err.response.data.errors[key]);
                            }
                        }
                    } else if (err) {
                        errorMessage = err;
                    } else {
                        errorMessage = "An unknown error occurred";
                    }

                    dispatch("handleError", errorMessage);
                    reject(err);
                });
        });
    },

    extendUserSession: ({commit, state}) => {
        return new Promise((resolve, reject) => {
            commit("loginRequest");
            Vue.axios
                .post("/auth/extend-session", {
                    refresh_token: state.refresh_token,
                })
                .then(resp => {
                    commit("loginSuccess", resp.data);
                    resolve(resp.data.access_token);
                })
                .catch(err => {
                    commit("loginError", err);
                    reject(err);
                });
        });
    },

    startSessionTimer: ({commit, dispatch, state}) => {
        commit("checkSessionExpiration");

        if (!state.sessionTimerStarted) {
            commit("sessionTimerStarted");

            setInterval(() => {
                commit("updateCurrentTimestamp");

                if (state.now > state.expires_at) {
                    // token is expired, log user out and redirect them to the login screen
                    dispatch("logout");
                    return;
                }

                commit("checkSessionExpiration", dispatch);
            }, 1000 * 60); // run the session update
        }
    },
};

const mutations = {
    loginRequest: state => {
        state.status = "loading";
    },

    loginSuccess: (state, response) => {
        const accessToken = "Bearer " + response.access_token;
        const now = Math.floor(Date.now() / 1000);

        sessionStorage.setItem("access_token", accessToken);
        sessionStorage.setItem("refresh_token", response.refresh_token);
        sessionStorage.setItem("token_expires_at", response.expires_in + now);

        Vue.axios.defaults.headers.common["Authorization"] = accessToken;

        state.status = "auth success";
        state.access_token = accessToken;
        state.refresh_token = response.refresh_token;
        state.expires_at = response.expires_in + now;
        state.hasLoadedOnce = true;
        state.showSessionWarning = false;
    },

    logout: (state) => {
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("token_expires_at");
        sessionStorage.removeItem("user_profile");
        Vue.axios.defaults.headers.common["Authorization"] = null;
        state.status = "logged out";
        state.access_token = null;
        state.refresh_token = null;
        state.expires_at = 0;
        state.showSessionWarning = false;

        return Promise.resolve(true);
    },

    sessionTimerStarted: state => {
        state.sessionTimerStarted = true;
    },

    checkSessionExpiration: state => {
        const expires_at = Number(state.expires_at);

        if (!!state.access_token && (expires_at < (state.now + LOGOUT_WARNING_THRESHOLD))) {
            state.showSessionWarning = true;
            return;
        }

        state.showSessionWarning = false;
    },

    updateCurrentTimestamp: state => {
        state.now = Math.floor(Date.now() / 1000);
    },
};

export default {
    state,
    actions,
    mutations,
};